/*
 * @author        zach.william@zumper.com (Zach William)
 * @copyright     Copyright (c) 2022, Zumper
 * @description   CarouselV3Base.tsx
 */
import { Box } from '@chakra-ui/react'
import type { Interpolation } from '@emotion/serialize'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Carousel, { CarouselInternalState } from 'react-multi-carousel'

import 'react-multi-carousel/lib/styles.css'
import { CarouselV3Controls } from './CarouselV3Controls/CarouselV3Controls'
import {
  LeftArrowDefault,
  ProgressDotsDefault,
  RightArrowDefault,
} from './CarouselV3Controls/CarouselV3ControlsDefaults'
import {
  CAROUSEL_CONTAINER_CLASS,
  CAROUSEL_DEVICE_TYPES,
  CAROUSEL_ITEM_CLASS,
  CAROUSEL_RESPONSIVE_DEFAULTS,
  CAROUSEL_SLIDER_CLASS,
} from './constants'
import * as styles from './style'
import { CarouselV3BaseProps } from './types'
import { useImagePreload } from './useImagePreload'

export const CarouselV3Base = ({
  // Custom props
  children,
  isMobile,
  maxDots,
  preloadImgUrls = [],
  hideButtons = false,
  initialIndex,
  setRef,
  setCarouselState,

  RightArrow = RightArrowDefault,
  LeftArrow = LeftArrowDefault,
  ProgressDots = ProgressDotsDefault,

  itemCss = styles.carouselItem,
  containerCss = styles.carouselContainer,
  sliderCss = styles.carouselSlider,
  wrapperCss = styles.carouselWrapper,

  // direct to lib props
  deviceType: deviceTypeProp,
  responsive = CAROUSEL_RESPONSIVE_DEFAULTS,
  afterChange,

  ...rest
}: CarouselV3BaseProps) => {
  const preloadImages = useImagePreload(preloadImgUrls)
  const [touched, setTouched] = useState(false)
  const [hasSlided, setHasSlided] = useState(false)

  const handleAfterChange = useCallback(
    (previousSlide: number, state: CarouselInternalState) => {
      preloadImages(previousSlide, state.currentSlide)
      setCarouselState(state)
      setTouched(true)
      afterChange?.(previousSlide, state)
    },
    [preloadImages, setCarouselState, afterChange]
  )

  const deviceType = useMemo(() => {
    if (deviceTypeProp) {
      return deviceTypeProp
    }
    return isMobile
      ? CAROUSEL_DEVICE_TYPES.MOBILE
      : CAROUSEL_DEVICE_TYPES.DESKTOP
  }, [deviceTypeProp, isMobile])

  // load the 2nd image client side on "mount"
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    preloadImages(0, 0)
  }, [preloadImages, preloadImgUrls.length])

  if (!Array.isArray(children) || children.length === 1) {
    return <Box css={wrapperCss as Interpolation<object>}>{children}</Box>
  }

  return (
    <Box
      css={
        styles.carouselWrapperStyle({
          wrapperCss,
          itemCss,
          containerCss,
          sliderCss,
        }) as Interpolation<object>
      }
    >
      <Carousel
        afterChange={handleAfterChange}
        arrows={false}
        autoPlay={false}
        beforeChange={() => setHasSlided(true)}
        containerClass={CAROUSEL_CONTAINER_CLASS}
        customButtonGroup={
          // @ts-expect-error the carousel component adds the missing props here: https://github.com/YIZHUANG/react-multi-carousel/blob/master/src/Carousel.tsx#L739
          // the type do not reflect that, I want to leave the missing props required because they are and will always be there
          <CarouselV3Controls
            LeftArrow={LeftArrow}
            ProgressDots={ProgressDots}
            RightArrow={RightArrow}
            hideButtons={hideButtons}
            isMobile={isMobile}
            maxDots={maxDots}
          />
        }
        deviceType={deviceType}
        draggable={true}
        infinite={false} // See README.md for details
        itemClass={CAROUSEL_ITEM_CLASS}
        keyBoardControl={true}
        ref={(ref) => {
          setRef(ref, initialIndex, hasSlided)
        }}
        responsive={responsive}
        showDots={false}
        sliderClass={CAROUSEL_SLIDER_CLASS}
        ssr={true}
        swipeable={true}
        transitionDuration={300}
        {...rest}
      >
        {React.Children.map(children, (child, index) =>
          index || touched || !initialIndex ? (
            child
          ) : (
            <Box visibility="hidden">{child}</Box>
          )
        )}
      </Carousel>
    </Box>
  )
}
